import * as THREE from 'three';
import { staticBoxesOf, type BoxDef, type BuildingDef, type DecorDef, type MapDef } from '@kokun/shared';
import { canvasTexture, toon } from '../core/materials';

export interface CityView {
  root: THREE.Group;
  movers: Map<string, THREE.Mesh>;
  vanish: Map<string, THREE.Mesh>;
  lampBulbs: THREE.Mesh[];
  clouds: THREE.Object3D[];
  balloons: THREE.Object3D[];
  waters: THREE.Mesh[];
  jumpPads: THREE.Mesh[];
  clockCtx: { ctx: CanvasRenderingContext2D; tex: THREE.CanvasTexture } | null;
  update(dt: number, t: number): void;
}

const G = {
  box: new THREE.BoxGeometry(1, 1, 1),
  cyl: new THREE.CylinderGeometry(1, 1, 1, 16),
  sphere: new THREE.SphereGeometry(1, 16, 12),
  cone: new THREE.ConeGeometry(1, 1, 12),
};

function place(m: THREE.Mesh, min: number[], max: number[]) {
  m.scale.set(max[0] - min[0], max[1] - min[1], max[2] - min[2]);
  m.position.set((min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2);
  return m;
}

const windowTexture = () =>
  canvasTexture(128, 128, (c) => {
    c.fillStyle = '#ffffff';
    c.fillRect(0, 0, 128, 128);
    c.fillStyle = '#7fb7d9';
    c.fillRect(22, 26, 32, 40);
    c.fillRect(74, 26, 32, 40);
    c.fillStyle = '#e9f5ff';
    c.fillRect(26, 30, 10, 14);
    c.fillRect(78, 30, 10, 14);
    c.fillStyle = 'rgba(0,0,0,0.12)';
    c.fillRect(0, 120, 128, 8);
  });

function signTexture(text: string, color: number) {
  return canvasTexture(1024, 160, (c) => {
    c.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
    c.beginPath();
    c.roundRect(6, 6, 1012, 148, 40);
    c.fill();
    c.strokeStyle = '#ffffff';
    c.lineWidth = 10;
    c.stroke();
    c.fillStyle = '#ffffff';
    c.font = '900 88px "Baloo 2", "Trebuchet MS", sans-serif';
    c.textAlign = 'center';
    c.textBaseline = 'middle';
    let size = 88;
    while (c.measureText(text).width > 960 && size > 30) {
      size -= 4;
      c.font = `900 ${size}px "Baloo 2", "Trebuchet MS", sans-serif`;
    }
    c.fillText(text, 512, 84);
  });
}

function building(b: BuildingDef, winTex: THREE.Texture, root: THREE.Group) {
  const w = b.max[0] - b.min[0];
  const d = b.max[1] - b.min[1];
  const cx = (b.min[0] + b.max[0]) / 2;
  const cz = (b.min[1] + b.max[1]) / 2;
  const tex = winTex.clone();
  tex.needsUpdate = true;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(Math.max(1, Math.round(w / 4)), Math.max(1, Math.round(b.height / 3.5)));
  const body = new THREE.Mesh(G.box, new THREE.MeshToonMaterial({ color: b.color, map: tex }));
  place(body, [b.min[0], 0, b.min[1]], [b.max[0], b.height, b.max[1]]);
  body.castShadow = body.receiveShadow = true;
  root.add(body);

  // Zócalo y puerta (fachada que mira a la plaza)
  const base = new THREE.Mesh(G.box, toon(0x6c757d));
  place(base, [b.min[0] - 0.1, 0, b.min[1] - 0.1], [b.max[0] + 0.1, 0.6, b.max[1] + 0.1]);
  root.add(base);
  const towardX = Math.abs(cx) > Math.abs(cz);
  const sx = towardX ? -Math.sign(cx) : 0;
  const sz = towardX ? 0 : -Math.sign(cz) || 1;
  const faceX = sx ? (sx > 0 ? b.max[0] : b.min[0]) : cx;
  const faceZ = sz ? (sz > 0 ? b.max[1] : b.min[1]) : cz;
  const door = new THREE.Mesh(G.box, toon(0x6d4c41));
  door.scale.set(sx ? 0.2 : 2, 3, sz ? 0.2 : 2);
  door.position.set(faceX + sx * 0.05, 1.5, faceZ + sz * 0.05);
  root.add(door);

  if (b.sign) {
    const sign = new THREE.Mesh(new THREE.PlaneGeometry(Math.min(12, (towardX ? d : w) * 0.85), 1.6), new THREE.MeshBasicMaterial({ map: signTexture(b.sign, b.signColor ?? 0x333333), transparent: true }));
    sign.position.set(faceX + sx * 0.15, Math.min(b.height - 1.2, 4.6), faceZ + sz * 0.15);
    sign.rotation.y = sx ? (sx > 0 ? Math.PI / 2 : -Math.PI / 2) : sz > 0 ? 0 : Math.PI;
    root.add(sign);
    // Toldo de rayas
    const awning = new THREE.Mesh(
      G.box,
      new THREE.MeshToonMaterial({
        map: canvasTexture(64, 16, (c) => {
          for (let i = 0; i < 8; i++) {
            c.fillStyle = i % 2 ? '#ffffff' : `#${(b.signColor ?? 0xe63946).toString(16).padStart(6, '0')}`;
            c.fillRect(i * 8, 0, 8, 16);
          }
        }),
      }),
    );
    awning.scale.set(sx ? 1.6 : 5, 0.15, sz ? 1.6 : 5);
    awning.position.set(faceX + sx * 0.8, 3.4, faceZ + sz * 0.8);
    awning.rotation.set(sz * 0.25, 0, -sx * 0.25);
    root.add(awning);
  }

  // Tejados
  const roofM = toon(b.roofColor);
  if (b.roof === 'flat') {
    const rim = [
      [b.min[0], b.min[1], b.max[0], b.min[1] + 0.3],
      [b.min[0], b.max[1] - 0.3, b.max[0], b.max[1]],
      [b.min[0], b.min[1], b.min[0] + 0.3, b.max[1]],
      [b.max[0] - 0.3, b.min[1], b.max[0], b.max[1]],
    ];
    for (const [x0, z0, x1, z1] of rim) {
      const m = new THREE.Mesh(G.box, roofM);
      place(m, [x0, b.height, z0], [x1, b.height + 0.35, z1]);
      root.add(m);
    }
    const top = new THREE.Mesh(G.box, toon(0x9aa5b1));
    place(top, [b.min[0] + 0.3, b.height - 0.02, b.min[1] + 0.3], [b.max[0] - 0.3, b.height + 0.02, b.max[1] - 0.3]);
    top.receiveShadow = true;
    root.add(top);
    // Chimenea / depósito decorativo
    const tank = new THREE.Mesh(G.cyl, toon(0xadb5bd));
    tank.scale.set(0.9, 1.6, 0.9);
    tank.position.set(b.max[0] - 1.5, b.height + 0.8, b.max[1] - 1.5);
    tank.castShadow = true;
    root.add(tank);
  } else if (b.roof === 'gable') {
    const shape = new THREE.Shape();
    shape.moveTo(-w / 2 - 0.5, 0);
    shape.lineTo(0, Math.min(5, w * 0.35));
    shape.lineTo(w / 2 + 0.5, 0);
    shape.closePath();
    const g = new THREE.ExtrudeGeometry(shape, { depth: d + 1, bevelEnabled: false });
    g.translate(0, 0, -(d + 1) / 2);
    const roof = new THREE.Mesh(g, roofM);
    roof.position.set(cx, b.height, cz);
    roof.castShadow = true;
    root.add(roof);
  } else {
    const dome = new THREE.Mesh(new THREE.SphereGeometry(Math.min(w, d) / 2, 24, 12, 0, Math.PI * 2, 0, Math.PI / 2), toon(b.roofColor, { opacity: 0.85 }));
    dome.position.set(cx, b.height, cz);
    root.add(dome);
  }
}

function boxMesh(b: BoxDef): THREE.Object3D | null {
  const c = b.color;
  switch (b.style) {
    case 'invisible':
    case 'ground':
    case 'building':
      return null;
    case 'fountain': {
      const g = new THREE.Group();
      const m = place(new THREE.Mesh(G.box, toon(c ?? 0xe9ecef)), b.min, b.max);
      m.castShadow = m.receiveShadow = true;
      g.add(m);
      return g;
    }
    case 'trunk': {
      const m = new THREE.Mesh(G.cyl, toon(0x8d5524));
      m.scale.set(0.28, b.max[1] - b.min[1], 0.28);
      m.position.set((b.min[0] + b.max[0]) / 2, (b.max[1] + b.min[1]) / 2, (b.min[2] + b.max[2]) / 2);
      m.castShadow = true;
      return m;
    }
    case 'post': {
      const m = new THREE.Mesh(G.cyl, toon(c ?? 0x495057));
      m.scale.set((b.max[0] - b.min[0]) / 2, b.max[1] - b.min[1], (b.max[2] - b.min[2]) / 2);
      m.position.set((b.min[0] + b.max[0]) / 2, (b.max[1] + b.min[1]) / 2, (b.min[2] + b.max[2]) / 2);
      m.castShadow = true;
      return m;
    }
    case 'pedestal': {
      const m = new THREE.Mesh(G.cyl, toon(c ?? 0xffffff));
      m.scale.set(0.5, b.max[1] - b.min[1], 0.5);
      m.position.set((b.min[0] + b.max[0]) / 2, (b.max[1] + b.min[1]) / 2, (b.min[2] + b.max[2]) / 2);
      return m;
    }
    case 'ice': {
      const m = place(new THREE.Mesh(G.box, toon(0xbde0fe, { opacity: 0.85, emissive: 0x223344 })), b.min, b.max);
      m.receiveShadow = true;
      return m;
    }
    case 'jumpPad': {
      const m = place(new THREE.Mesh(G.box, toon(0xff006e, { emissive: 0x550022 })), b.min, b.max);
      m.userData.jumpPad = true;
      return m;
    }
    case 'bench':
      return place(new THREE.Mesh(G.box, toon(0xa0522d)), b.min, b.max);
    case 'steps':
      return place(new THREE.Mesh(G.box, toon(c ?? 0xc68b59)), b.min, b.max);
    case 'wall':
      return place(new THREE.Mesh(G.box, toon(c ?? 0xb56576)), b.min, b.max);
    default: {
      const m = place(new THREE.Mesh(G.box, toon(c ?? 0xdee2e6)), b.min, b.max);
      m.castShadow = m.receiveShadow = true;
      return m;
    }
  }
}

function decorMesh(d: DecorDef): THREE.Object3D {
  const g = new THREE.Group();
  g.position.set(...d.pos);
  const s = d.scale ?? 1;
  switch (d.kind) {
    case 'tree':
      for (const [x, y, z, r] of [
        [0, 3.6, 0, 1.6],
        [0.8, 3.1, 0.3, 1.1],
        [-0.7, 3.3, -0.4, 1.2],
      ]) {
        const m = new THREE.Mesh(G.sphere, toon(0x52b788));
        m.position.set(x * s, y * s, z * s);
        m.scale.setScalar(r * s);
        m.castShadow = true;
        g.add(m);
      }
      break;
    case 'pine':
      for (let i = 0; i < 3; i++) {
        const m = new THREE.Mesh(G.cone, toon(0x2d6a4f));
        m.position.y = (2.6 + i * 1.1) * s;
        m.scale.set((1.8 - i * 0.45) * s, 1.8 * s, (1.8 - i * 0.45) * s);
        m.castShadow = true;
        g.add(m);
      }
      break;
    case 'bush':
      for (let i = 0; i < 4; i++) {
        const m = new THREE.Mesh(G.sphere, toon(0x40916c));
        m.position.set(Math.cos(i * 1.7) * 0.5 * s, 0.55 * s, Math.sin(i * 1.7) * 0.5 * s);
        m.scale.setScalar(0.75 * s);
        g.add(m);
      }
      g.userData.bush = true;
      break;
    case 'lamp': {
      const bulb = new THREE.Mesh(G.sphere, new THREE.MeshBasicMaterial({ color: 0xfff3b0 }));
      bulb.position.y = 3.8;
      bulb.scale.setScalar(0.32);
      g.add(bulb);
      g.userData.bulb = bulb;
      break;
    }
    case 'flowers':
      for (let i = 0; i < 14; i++) {
        const m = new THREE.Mesh(G.sphere, toon([0xff006e, 0xffbe0b, 0xffffff, 0x8338ec][i % 4]));
        m.position.set(Math.cos(i * 2.4) * (0.4 + (i % 5) * 0.3), 0.18, Math.sin(i * 2.4) * (0.4 + (i % 5) * 0.3));
        m.scale.setScalar(0.14);
        g.add(m);
      }
      break;
    case 'pond': {
      const m = new THREE.Mesh(new THREE.CircleGeometry(s, 32), toon(0x48cae4, { opacity: 0.85, emissive: 0x0a3d62 }));
      m.rotation.x = -Math.PI / 2;
      g.add(m);
      g.userData.water = m;
      break;
    }
    case 'cloud':
      for (let i = 0; i < 5; i++) {
        const m = new THREE.Mesh(G.sphere, new THREE.MeshBasicMaterial({ color: 0xffffff, fog: false }));
        m.position.set((i - 2) * 2.2 * s, Math.sin(i) * 0.6, (i % 2) * s);
        m.scale.setScalar((2 + (i % 3) * 0.7) * s);
        g.add(m);
      }
      g.userData.cloud = true;
      break;
    case 'balloon': {
      const m = new THREE.Mesh(G.sphere, toon(d.color ?? 0xff006e));
      m.scale.set(0.9, 1.1, 0.9);
      m.position.y = 4;
      g.add(m);
      const str = new THREE.Mesh(G.cyl, toon(0xffffff));
      str.scale.set(0.02, 3, 0.02);
      str.position.y = 1.5;
      g.add(str);
      g.userData.balloon = true;
      break;
    }
    case 'umbrella': {
      const pole = new THREE.Mesh(G.cyl, toon(0xffffff));
      pole.scale.set(0.06, 2.6, 0.06);
      pole.position.y = 1.3;
      const top = new THREE.Mesh(new THREE.ConeGeometry(1.8, 0.7, 10), toon(d.color ?? 0xff6b6b));
      top.position.y = 2.7;
      g.add(pole, top);
      break;
    }
    case 'clockTower': {
      const tower = new THREE.Mesh(G.box, toon(0xfff1d6));
      tower.scale.set(4, 8, 4);
      tower.position.y = 4;
      tower.castShadow = true;
      const roof = new THREE.Mesh(new THREE.ConeGeometry(3.2, 3, 4), toon(0xe63946));
      roof.position.y = 9.5;
      roof.rotation.y = Math.PI / 4;
      g.add(tower, roof);
      break;
    }
  }
  return g;
}

export function buildCity(map: MapDef): CityView {
  const root = new THREE.Group();
  const winTex = windowTexture();

  // Suelo base y áreas pintadas
  const ground = new THREE.Mesh(new THREE.PlaneGeometry(400, 400), toon(0x74c69d));
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  root.add(ground);
  const paintColor = { road: 0x495057, plaza: 0xf1e3d3, grass: 0x95d5b2, sand: 0xf4d58d, stripe: 0xffffff };
  map.paint.forEach((p, i) => {
    const w = p.max[0] - p.min[0];
    const d = p.max[1] - p.min[1];
    const y = 0.01 + i * 0.002;
    const geo = p.circle ? new THREE.CircleGeometry(w / 2, 48) : new THREE.PlaneGeometry(w, d);
    const m = new THREE.Mesh(geo, toon(paintColor[p.kind]));
    m.rotation.x = -Math.PI / 2;
    m.position.set((p.min[0] + p.max[0]) / 2, y, (p.min[1] + p.max[1]) / 2);
    m.receiveShadow = true;
    root.add(m);
    if (p.kind === 'road') {
      // Líneas discontinuas con instancias (una sola llamada de dibujo por carretera)
      const along = w > d;
      const len = along ? w : d;
      const n = Math.floor(len / 4);
      const dash = new THREE.InstancedMesh(new THREE.PlaneGeometry(along ? 1.6 : 0.2, along ? 0.2 : 1.6), toon(0xffffff), n);
      const mtx = new THREE.Matrix4();
      const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.PI / 2, 0, 0));
      for (let k = 0; k < n; k++) {
        const pos = new THREE.Vector3(along ? p.min[0] + k * 4 + 2 : (p.min[0] + p.max[0]) / 2, y + 0.005, along ? (p.min[1] + p.max[1]) / 2 : p.min[1] + k * 4 + 2);
        dash.setMatrixAt(k, mtx.compose(pos, q, new THREE.Vector3(1, 1, 1)));
      }
      root.add(dash);
    }
    if (p.kind === 'plaza') {
      for (const r of [5, 9.5]) {
        const ring = new THREE.Mesh(new THREE.RingGeometry(r, r + 0.35, 48), toon(0xd4a373));
        ring.rotation.x = -Math.PI / 2;
        ring.position.set(0, y + 0.004, 0);
        root.add(ring);
      }
    }
  });

  // Colinas lejanas (fuera de los límites jugables)
  for (let i = 0; i < 18; i++) {
    const a = (i / 18) * Math.PI * 2;
    const hill = new THREE.Mesh(G.sphere, toon(i % 2 ? 0x52b788 : 0x40916c));
    const r = 18 + (i % 4) * 6;
    hill.position.set(Math.cos(a) * (95 + (i % 3) * 10), -r * 0.5, Math.sin(a) * (95 + (i % 3) * 10));
    hill.scale.set(r * 1.4, r, r * 1.4);
    root.add(hill);
  }
  // Valla perimetral
  const fenceM = toon(0xffffff);
  for (let i = -70; i <= 70; i += 3.5) {
    for (const [x, z] of [
      [i, -70],
      [i, 70],
      [-70, i],
      [70, i],
    ]) {
      const post = new THREE.Mesh(G.box, fenceM);
      post.scale.set(0.2, 1.2, 0.2);
      post.position.set(x, 0.6, z);
      root.add(post);
    }
  }

  for (const b of map.buildings) building(b, winTex, root);
  for (const b of staticBoxesOf(map)) {
    const m = boxMesh(b);
    if (m) root.add(m);
  }

  const lampBulbs: THREE.Mesh[] = [];
  const clouds: THREE.Object3D[] = [];
  const balloons: THREE.Object3D[] = [];
  const waters: THREE.Mesh[] = [];
  for (const d of map.decor) {
    const o = decorMesh(d);
    if (o.userData.bulb) lampBulbs.push(o.userData.bulb);
    if (o.userData.cloud) clouds.push(o);
    if (o.userData.balloon) balloons.push(o);
    if (o.userData.water) waters.push(o.userData.water);
    root.add(o);
  }

  // Reloj real en la torre del ayuntamiento
  let clockCtx: CityView['clockCtx'] = null;
  const tower = map.decor.find((d) => d.kind === 'clockTower');
  if (tower) {
    const c = document.createElement('canvas');
    c.width = c.height = 128;
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    const face = new THREE.Mesh(new THREE.CircleGeometry(1.5, 32), new THREE.MeshBasicMaterial({ map: tex }));
    face.position.set(tower.pos[0], tower.pos[1] + 6, tower.pos[2] + 2.02);
    root.add(face);
    clockCtx = { ctx: c.getContext('2d')!, tex };
  }

  // Plataformas móviles y que desaparecen
  const movers = new Map<string, THREE.Mesh>();
  for (const p of map.movingPlatforms) {
    const m = new THREE.Mesh(G.box, toon(p.color, { emissive: 0x111111 }));
    m.scale.set(...p.size);
    m.castShadow = m.receiveShadow = true;
    movers.set(p.id, m);
    root.add(m);
  }
  const vanish = new Map<string, THREE.Mesh>();
  for (const v of map.vanishPlatforms) {
    const m = place(new THREE.Mesh(G.box, new THREE.MeshToonMaterial({ color: v.color, transparent: true, opacity: 0.95 })), v.min, v.max);
    vanish.set(v.id, m);
    root.add(m);
  }

  const jumpPads = root.children.filter((o) => o.userData.jumpPad) as THREE.Mesh[];
  let lastClock = -1;

  return {
    root,
    movers,
    vanish,
    lampBulbs,
    clouds,
    balloons,
    waters,
    jumpPads,
    clockCtx,
    update(dt: number, t: number) {
      clouds.forEach((c, i) => {
        c.position.x += dt * (0.6 + i * 0.1);
        if (c.position.x > 110) c.position.x = -110;
      });
      balloons.forEach((b, i) => (b.position.y = Math.sin(t * 1.5 + i) * 0.4));
      jumpPads.forEach((p) => ((p.material as THREE.MeshToonMaterial).emissiveIntensity = 0.6 + Math.sin(t * 6) * 0.4));
      if (clockCtx && Math.floor(t) !== lastClock) {
        lastClock = Math.floor(t);
        const now = new Date();
        const { ctx } = clockCtx;
        ctx.fillStyle = '#fffdf5';
        ctx.beginPath();
        ctx.arc(64, 64, 62, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#1d3557';
        ctx.lineWidth = 6;
        ctx.stroke();
        const hand = (a: number, len: number, w: number) => {
          ctx.lineWidth = w;
          ctx.beginPath();
          ctx.moveTo(64, 64);
          ctx.lineTo(64 + Math.sin(a) * len, 64 - Math.cos(a) * len);
          ctx.stroke();
        };
        hand(((now.getHours() % 12) / 12 + now.getMinutes() / 720) * Math.PI * 2, 30, 7);
        hand((now.getMinutes() / 60) * Math.PI * 2, 46, 4);
        clockCtx.tex.needsUpdate = true;
      }
    },
  };
}
